import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Insights = ({ activeCategory = "all", onCategoryChange = null }) => {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => setPosts(res.data)).catch(() => setPosts([]));
  }, []);

  // Extract unique categories from posts
  const pick = (p, field) => p?.[`${field}_${lang}`] || p?.[`${field}_it`] || p?.[`${field}_en`] || "";
  const categories = ["all", ...Array.from(new Set(posts.map((p) => pick(p, "category"))))];
  const filtered = activeCategory === "all" ? posts : posts.filter((p) => pick(p, "category") === activeCategory);

  return (
    <section
      id="insights"
      className="relative bg-[#F1EEE8] text-[#232326] py-16 md:py-24"
      data-testid="insights-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 text-[#13535A] text-[0.7rem] tracking-[0.28em] uppercase font-semibold">
              <span className="w-6 h-px bg-[#8A6F45]" />
              <span>{t.insights.overline}</span>
            </div>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.8rem)] whitespace-pre-line">
              {t.insights.title}
            </h2>
            {t.insights.lead && (
              <p className="text-[#232326]/70 text-lg leading-relaxed max-w-2xl mt-6">
                {t.insights.lead}
              </p>
            )}
          </div>
        </div>

        {/* Category filter */}
        {onCategoryChange && posts.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-14" data-testid="blog-categories">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => onCategoryChange(cat)}
                className={`px-5 py-2 text-[0.66rem] tracking-[0.22em] uppercase font-medium border transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#13535A] text-[#F1EEE8] border-[#13535A]"
                    : "bg-transparent text-[#232326]/75 border-[#232326]/25 hover:border-[#13535A] hover:text-[#13535A]"
                }`}
                data-testid={`blog-cat-${cat}`}
              >
                {cat === "all" ? (lang === "it" ? "Tutti" : "All") : cat}
              </button>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={revealUp}
              custom={i}
              data-testid={`insight-${p.slug}`}
            >
              <Link to={`/blog/${p.slug}`} className="group block">
                <div className="relative overflow-hidden clip-card aspect-[4/3] mb-6 bg-[#E6E3DC]">
                  <img
                    src={`${p.image}?auto=format&fit=crop&w=1200&q=80`}
                    alt={pick(p, "title")}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-all duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-[0.66rem] tracking-[0.28em] uppercase text-[#F1EEE8] border border-[#F1EEE8]/35 px-3 py-1 bg-[#0A0A0B]/40 backdrop-blur-sm">
                    {pick(p, "category")}
                  </div>
                </div>
                <div className="flex items-baseline gap-4 text-[#8A6F45] text-[0.68rem] tracking-[0.24em] uppercase mb-4 font-medium">
                  <span>{p.date}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight mb-3 group-hover:text-[#13535A] transition-colors">
                  {pick(p, "title")}
                </h3>
                <p className="text-[#232326]/70 leading-relaxed mb-5 text-[0.98rem]">
                  {pick(p, "excerpt")}
                </p>
                <span className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase text-[#232326] group-hover:text-[#13535A] transition-colors font-medium">
                  {t.insights.readMore}
                  <ArrowUpRight size={14} weight="light" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
;
